import React from "react";
// Energy Block Dashboard page

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomInput from "../../forms/input/Input";
import DatePickerInput from "../../forms/input/DatePickerInput";
import ContainedButton from "../../forms/button/ContainedButton";
import EnergySourcePieChart from "../charts/EnergySourcePieChart";
import CustomDataTable from "../../components/table/CustomTable";

// React-icons
import { FaShoppingCart } from "react-icons/fa";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const EnergyBlockDashboard: React.FC = () => {
  const COLUMNS = [
    "Date",
    "Energy Block Purchased",
    "Source",
    "Total Cost",
    "Download",
  ];

  const energyBlocksData = [
    {
      title: "Available energy blocks",
      subTitle: "5 kWh",
    },
    {
      title: "Price",
      subTitle: "₹4.50/kWh",
    },
    {
      title: "Total Cost",
      subTitle: "₹22.50",
    },
  ];

  const purchaseHistoryData = [
    {
      date: "2024-08-20",
      energyPurchased: "100 kWh",
      source: "Renewable",
      totalCost: "₹450",
    },
    {
      date: "2024-08-21",
      energyPurchased: "200 kWh",
      source: "Non-Renewable",
      totalCost: "₹900",
    },
    {
      date: "2024-08-22",
      energyPurchased: "150 kWh",
      source: "Renewable",
      totalCost: "₹675",
    },
    {
      date: "2024-08-23",
      energyPurchased: "250 kWh",
      source: "Non-Renewable",
      totalCost: "₹1125",
    },
    {
      date: "2024-08-24",
      energyPurchased: "300 kWh",
      source: "Renewable",
      totalCost: "₹1350",
    },
  ];

  const rowsContent = purchaseHistoryData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">{item.date}</Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.energyPurchased}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.source}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.totalCost}
        </Typography>
      </TableCell>
      <TableCell>
        <ContainedButton
          label={"Download"}
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
        />
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <Card>
        <CardHeader
          title={<Typography variant="h5">Available Energy Blocks</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <DatePickerInput
                label={"Select Time"}
                placeholder={"dd/mm/yyyy hh:mm"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomInput
                label={"Energy to Buy (kWh)"}
                placeholder={"Energy to Buy (kWh)"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomInput
                label={"Price (₹/kWh)"}
                placeholder={"Price (₹/kWh)"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3.99, lg: 3.99 }}>
              <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
                <Typography variant="h5" mt={"25px"}>
                  =
                </Typography>
                <CustomInput
                  label={"Total Cost"}
                  placeholder={"Total Cost"}
                  disabled={true}
                />
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2.25} mt={"50px"}>
            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Filter by Size"}
                options={[{ label: "350 kWh", value: "350kWh" }]}
                label={"Filter by Size"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Filter By Price"}
                options={[{ label: "₹50", value: "₹50" }]}
                label={"Filter By Price"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Sort by Price"}
                options={[{ label: "Low to High", value: "Low to High" }]}
                label={"Sort by Price"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Sort by Availability"}
                options={[{ label: "Available", value: "Available" }]}
                label={"Sort by Availability"}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.25} mt={"50px"}>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                  maxHeight: "400px",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <ContainedButton
                        label={"Available"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                      />
                      <ContainedButton
                        label={"Purchase"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2.25}>
                    {energyBlocksData.map((item) => {
                      return (
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                          <Box
                            sx={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #E0E0E0",
                              borderRadius: "16px",
                              p: 2,
                              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                              textAlign: "center",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <Box
                              sx={{
                                background: "#0396FF",
                                width: "fit-content",
                                padding: "16px 16px 9px 16px",
                                borderRadius: "5px",
                              }}
                            >
                              <UnifiedIcon
                                icon={FaShoppingCart}
                                color="#ffffff"
                                fontSize="30px"
                              />
                            </Box>

                            <Stack>
                              <Typography
                                variant="body1"
                                fontSize={"17px"}
                                textAlign={"left"}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontSize={"17px"}
                                fontWeight={600}
                                textAlign={"left"}
                                mt={"10px"}
                              >
                                {item.subTitle}
                              </Typography>
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                  maxHeight: "400px",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <ContainedButton
                        label={"Sold Out"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                      />
                      <ContainedButton
                        label={"Purchase"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                      />
                    </Stack>
                  }
                />

                <Divider />
                <CardContent>
                  <Grid container spacing={2.25}>
                    {energyBlocksData.map((item) => {
                      return (
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                          <Box
                            sx={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #E0E0E0",
                              borderRadius: "16px",
                              p: 2,
                              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                              textAlign: "center",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <Box
                              sx={{
                                background: "#0396FF",
                                width: "fit-content",
                                padding: "16px 16px 9px 16px",
                                borderRadius: "5px",
                              }}
                            >
                              <UnifiedIcon
                                icon={FaShoppingCart}
                                color="#ffffff"
                                fontSize="30px"
                              />
                            </Box>

                            <Stack>
                              <Typography
                                variant="body1"
                                fontSize={"17px"}
                                textAlign={"left"}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontSize={"17px"}
                                fontWeight={600}
                                textAlign={"left"}
                                mt={"10px"}
                              >
                                {item.subTitle}
                              </Typography>
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Energy Source</Typography>}
        />
        <Divider />

        <CardContent>
          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            <FormControlLabel
              value="Renewable Energy"
              control={
                <Radio
                  sx={{
                    "&.Mui-checked": {
                      color: "#0396FF",
                    },
                  }}
                />
              }
              label="Renewable Energy"
            />
            <FormControlLabel
              value="Non-Renewable Energy"
              control={
                <Radio
                  sx={{
                    "&.Mui-checked": {
                      color: "#0396FF",
                    },
                  }}
                />
              }
              label="Non-Renewable Energy"
            />
          </Stack>

          <Grid container spacing={2.25} mt={"30px"}>
            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Filter by Size"}
                options={[{ label: "350 kWh", value: "350kWh" }]}
                label={"Filter by Size"}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Filter By Price"}
                options={[{ label: "₹50", value: "₹50" }]}
                label={"Filter By Price"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Sort by Price"}
                options={[{ label: "Low to High", value: "Low to High" }]}
                label={"Sort by Price"}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3, lg: 3 }}>
              <CustomOutlinedSelect
                placeholder={"Sort by Availability"}
                options={[{ label: "Available", value: "Available" }]}
                label={"Sort by Availability"}
              />
            </Grid>
          </Grid>

          <Typography
            variant="body1"
            fontSize={"17px"}
            fontWeight={500}
            mt={"30px"}
            mb={"10px"}
          >
            Energy By Source
          </Typography>

          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 5, lg: 5 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                  mt: "20px",
                  minHeight: "350px",
                  maxHeight: "350px",
                }}
              >
                <CardContent>
                  <EnergySourcePieChart />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 7, lg: 7 }}>
              <Card
                sx={{
                  border: "1px solid #D7DAE7",
                  mt: "20px",
                  maxHeight: "350px",
                }}
              >
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <ContainedButton
                        label={"Available Now"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                      />
                      <ContainedButton
                        label={"Purchase"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                      />
                    </Stack>
                  }
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2.25}>
                    {energyBlocksData.map((item) => {
                      return (
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                          <Box
                            sx={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #E0E0E0",
                              borderRadius: "16px",
                              p: 2,
                              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                              textAlign: "center",
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <Box
                              sx={{
                                background: "#0396FF",
                                width: "fit-content",
                                padding: "16px 16px 9px 16px",
                                borderRadius: "5px",
                              }}
                            >
                              <UnifiedIcon
                                icon={FaShoppingCart}
                                color="#ffffff"
                                fontSize="30px"
                              />
                            </Box>

                            <Stack>
                              <Typography
                                variant="body1"
                                fontSize={"17px"}
                                textAlign={"left"}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                variant="body1"
                                fontSize={"17px"}
                                fontWeight={600}
                                textAlign={"left"}
                                mt={"10px"}
                              >
                                {item.subTitle}
                              </Typography>
                            </Stack>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px", maxHeight: "400px" }}>
        <CardHeader
          title={<Typography variant="h5">Purchase History</Typography>}
        />
        <Divider />

        <CardContent>
          <CustomDataTable
            sx={{ pb: "35px", height: "350px" }}
            columns={COLUMNS}
            pagination={false}
            rowsContent={rowsContent}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default EnergyBlockDashboard;
